import React, { useState, useRef, useEffect, useCallback } from 'react';
import Papa from 'papaparse';
import {
    Edit2, Plus, UserX, UserCheck, Trash2,
    Upload, Download, CheckCircle, XCircle,
    AlertTriangle, X, FileText, Loader2,
    ChevronRight, RotateCcw, List
} from 'lucide-react';
import CustomSelect from './CustomSelect';
import { ZONAS_MALAGA, ZONAS_MALAGA_OPTIONS, normalizarZonaMalaga } from '../utils/zonasMalaga';

// ── Constantes ────────────────────────────────────────────────────────────────
const CABECERAS_CSV = [
    'nombre', 'email', 'telefono', 'whatsapp',
    'tipo_usuario', 'tipo_punto_critico',
    'zona', 'ciudad', 'pais',
    'rol', 'tipo_vulnerabilidad', 'medio_comunicacion_preferido',
    'suscrito_email', 'suscrito_sms', 'suscrito_whatsapp'
];

const EJEMPLO_FILAS = [
    {
        nombre: 'Juan García López', email: 'juan@ejemplo.com',
        telefono: '+34666111222', whatsapp: '+34666111222',
        tipo_usuario: 'Residente', tipo_punto_critico: '',
        zona: 'Distrito 1 — Centro', ciudad: 'Málaga', pais: 'España',
        rol: 'poblacion', tipo_vulnerabilidad: 'ninguna', medio_comunicacion_preferido: 'whatsapp',
        suscrito_email: 'si', suscrito_sms: 'si', suscrito_whatsapp: 'no'
    },
    {
        nombre: 'Hospital Central', email: 'contacto@hospitalcentral.es',
        telefono: '+34952000000', whatsapp: '',
        tipo_usuario: 'Institución', tipo_punto_critico: 'Hospital',
        zona: 'Distrito 4 — Bailén-Miraflores', ciudad: 'Málaga', pais: 'España',
        rol: 'gestor', tipo_vulnerabilidad: 'ninguna', medio_comunicacion_preferido: 'email',
        suscrito_email: 'si', suscrito_sms: 'no', suscrito_whatsapp: 'no'
    }
];

const ESTADOS_FINALES = ['COMPLETED', 'PARTIAL_SUCCESS', 'FAILED'];
const POLLING_INTERVAL_MS = 3000;

// ── Descarga de plantilla ─────────────────────────────────────────────────────
function descargarPlantilla() {
    const filas = [CABECERAS_CSV.join(',')];
    EJEMPLO_FILAS.forEach(row => {
        filas.push(CABECERAS_CSV.map(h => `"${(row[h] || '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([filas.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_contactos.csv';
    a.click();
    URL.revokeObjectURL(url);
}

// ── Barra de progreso ─────────────────────────────────────────────────────────
function BarraProgreso({ pct, estado }) {
    const colores = {
        PROCESSING:      'bg-blue-500',
        COMPLETED:       'bg-green-500',
        PARTIAL_SUCCESS: 'bg-amber-500',
        FAILED:          'bg-red-500',
        PENDING:         'bg-gray-400',
    };
    const color = colores[estado] || 'bg-blue-500';
    return (
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <div
                className={`h-3 rounded-full transition-all duration-500 ${color}`}
                style={{ width: `${Math.max(pct, estado === 'PROCESSING' ? 5 : 0)}%` }}
            />
        </div>
    );
}

// ── Badge de estado de job ────────────────────────────────────────────────────
function BadgeEstado({ estado }) {
    const config = {
        PENDING:         { label: 'En cola',       cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
        PROCESSING:      { label: 'Procesando…',   cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
        COMPLETED:       { label: 'Completado',    cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
        PARTIAL_SUCCESS: { label: 'Parcial',       cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
        FAILED:          { label: 'Fallido',       cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
    };
    const { label, cls } = config[estado] || config.PENDING;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
}

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════
const ContactsManager = ({
    contactos,
    nuevoContacto,
    setNuevoContacto,
    contactoEditando,
    guardarContacto,
    iniciarEdicionContacto,
    cancelarEdicionContacto,
    darDeBajaContacto,
    reactivarContacto,
    eliminarContacto,
    importarContactosCSV,
    consultarJobImportacion,
    descargarErroresImportacion,
    listas = [],
    fetchListas,
    fetchContactosDeLista,
    darkMode
}) => {

    // ── Estado del panel de importación ──────────────────────────────────────
    const [mostrarImport, setMostrarImport] = useState(false);

    // Paso 1 – selección y preview
    const [archivoFile,    setArchivoFile]    = useState(null);
    const [archivoNombre,  setArchivoNombre]  = useState('');
    const [nombreLista,    setNombreLista]    = useState('');
    const [preview,        setPreview]        = useState([]);
    const [erroresLocal,   setErroresLocal]   = useState([]);
    const [dragging,       setDragging]       = useState(false);
    const [errorPanel,     setErrorPanel]     = useState(null); // mensaje inline en lugar de alert()
    const inputRef = useRef(null);

    // Paso 2 – subida y progreso
    const [subiendo,   setSubiendo]   = useState(false);
    const [jobId,      setJobId]      = useState(null);
    const [jobEstado,  setJobEstado]  = useState(null);
    const pollingRef = useRef(null);

    // ── Estado de pestañas y vista de listas ─────────────────────────────────
    const [tabActiva,            setTabActiva]            = useState('contactos'); // 'contactos' | 'listas' | null
    const [busquedaContactos,    setBusquedaContactos]    = useState('');
    const [filtroZona,           setFiltroZona]           = useState('');
    const [busquedaListas,       setBusquedaListas]       = useState('');
    const [listaSeleccionada,    setListaSeleccionada]    = useState(null);        // objeto lista
    const [contactosDeLista,     setContactosDeLista]     = useState([]);
    const [cargandoContactosLista, setCargandoContactosLista] = useState(false);

    // ── Estado para modal "Añadir a lista" ──────────────────────────────
    const [modalListaContactoId, setModalListaContactoId] = useState(null);
    const [listaSeleccionadaParaAnadir, setListaSeleccionadaParaAnadir] = useState('');
    const [anadiendo, setAnadiendo] = useState(false);

    const handleAnadirALista = async () => {
        if (!listaSeleccionadaParaAnadir || !modalListaContactoId) return;
        setAnadiendo(true);
        try {
            const token = localStorage.getItem('token');
            const API = window.__API_BASE || 'https://warngen-api-352514293915.europe-west1.run.app';
            const res = await fetch(`${API}/api/listas/${listaSeleccionadaParaAnadir}/contactos`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ contactoIds: [modalListaContactoId] })
            });
            if (res.ok) {
                alert('Contacto añadido a la lista correctamente');
                setModalListaContactoId(null);
                setListaSeleccionadaParaAnadir('');
            } else {
                const data = await res.json();
                alert('Error: ' + (data.error || 'No se pudo añadir'));
            }
        } catch (e) {
            alert('Error de conexión');
        }
        setAnadiendo(false);
    };

    // ── Limpieza total ────────────────────────────────────────────────────────
    const resetear = useCallback(() => {
        setArchivoFile(null);
        setArchivoNombre('');
        setNombreLista('');
        setPreview([]);
        setErroresLocal([]);
        setErrorPanel(null);
        setSubiendo(false);
        setJobId(null);
        setJobEstado(null);
        if (inputRef.current) inputRef.current.value = '';
        if (pollingRef.current) clearInterval(pollingRef.current);
    }, []);

    // ── Cargar contactos de una lista seleccionada ────────────────────────────
    const verContactosDeLista = useCallback(async (lista) => {
        setListaSeleccionada(lista);
        setContactosDeLista([]);
        if (!lista || !fetchContactosDeLista) return;
        setCargandoContactosLista(true);
        try {
            const data = await fetchContactosDeLista(lista.grupo_id, 1, 500);
            if (data.success) setContactosDeLista(data.contactos || []);
        } catch (e) { console.error('Error cargando contactos de lista:', e); }
        finally { setCargandoContactosLista(false); }
    }, [fetchContactosDeLista]);

    // ── Detener polling al desmontar ──────────────────────────────────────────
    useEffect(() => {
        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, []);

    // ── Polling del estado del job ────────────────────────────────────────────
    const iniciarPolling = useCallback((id) => {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = setInterval(async () => {
            const res = await consultarJobImportacion(id);
            if (res.success) {
                setJobEstado(res.job);
                if (ESTADOS_FINALES.includes(res.job.status)) {
                    clearInterval(pollingRef.current);
                }
            }
        }, POLLING_INTERVAL_MS);
    }, [consultarJobImportacion]);

    // ── Procesar archivo seleccionado ─────────────────────────────────────────
    const procesarArchivo = (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setErrorPanel('El archivo debe tener extensión .csv');
            return;
        }
        setErrorPanel(null);
        setArchivoFile(file);
        setArchivoNombre(file.name);
        // Pre-rellenar nombre de lista con el nombre del archivo (sin extensión)
        if (!nombreLista) setNombreLista(file.name.replace(/\.csv$/i, ''));
        setJobId(null);
        setJobEstado(null);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: h => h.trim().toLowerCase().replace(/\s+/g, '_'),
            complete: ({ data, errors: parseErrors }) => {
                const realErrors = parseErrors.filter(e => e.code !== 'UndetectableDelimiter');
                if (realErrors.length > 0) {
                    setErrorPanel('Error al leer el CSV: ' + realErrors[0].message);
                    return;
                }
                const locales = [];
                data.forEach((row, i) => {
                    const fila = i + 2;
                    const nombre = String(row.nombre || '').trim();
                    const email  = String(row.email  || '').trim();
                    const tel    = String(row.telefono || '').trim();

                    if (!nombre) {
                        locales.push({ fila, motivo: 'Nombre vacío' });
                    } else if (!tel && !email && !String(row.whatsapp || '').trim()) {
                        locales.push({ fila, motivo: `"${nombre}": sin teléfono, email ni whatsapp` });
                    } else {
                        // Validaciones de formato (advertencia, no bloquean)
                        if (email && !email.includes('@')) {
                            locales.push({ fila, motivo: `"${nombre}": email inválido (${email})` });
                        } else if (tel && tel.replace(/\D/g, '').length < 7) {
                            locales.push({ fila, motivo: `"${nombre}": teléfono demasiado corto (${tel})` });
                        }
                    }
                });
                setPreview(data);
                setErroresLocal(locales);
            }
        });
    };

    const onFileChange = (e) => procesarArchivo(e.target.files[0]);

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        procesarArchivo(e.dataTransfer.files[0]);
    };

    // ── Confirmar importación: subir archivo a GCS ────────────────────────────
    const handleImportar = async () => {
        if (!archivoFile || preview.length === 0) return;
        if (!nombreLista.trim()) {
            setErrorPanel('Por favor, asigna un nombre a esta lista antes de importar.');
            return;
        }
        setErrorPanel(null);
        setSubiendo(true);
        try {
            const res = await importarContactosCSV(archivoFile, nombreLista.trim());
            if (res.success) {
                setJobId(res.jobId);
                setJobEstado({ status: 'PENDING', processed_records: 0, failed_records: 0, total_records: preview.length, progreso_pct: 0 });
                iniciarPolling(res.jobId);
            } else {
                setErrorPanel('Error al iniciar la importación: ' + (res.error || 'Error desconocido'));
            }
        } finally {
            setSubiendo(false);
        }
    };

    // ── Datos derivados ───────────────────────────────────────────────────────
    const filasBuenas    = preview.length - erroresLocal.length;
    const esFinalizado   = jobEstado && ESTADOS_FINALES.includes(jobEstado.status);
    const estaEnProgreso = jobEstado && !esFinalizado;

    // ═══════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════
    return (
        <div className="space-y-6">

            {/* ── Cabecera ── */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                    Gestión de Contactos
                </h2>
                <button
                    id="btn-importar-csv"
                    onClick={() => { setMostrarImport(v => !v); if (mostrarImport) resetear(); }}
                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg font-semibold transition shadow"
                >
                    <Upload size={18} />
                    {mostrarImport ? 'Cerrar importación' : 'Importar CSV'}
                </button>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                PANEL DE IMPORTACIÓN
            ══════════════════════════════════════════════════════════════ */}
            {mostrarImport && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-emerald-200 dark:border-emerald-800 overflow-hidden">

                    {/* Barra de título */}
                    <div className="flex items-center justify-between px-6 py-4 bg-emerald-50 dark:bg-emerald-900/30 border-b border-emerald-200 dark:border-emerald-800">
                        <div className="flex items-center gap-3">
                            <Upload className="text-emerald-600 dark:text-emerald-400" size={22} />
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                                Importación masiva de contactos
                            </h3>
                        </div>
                        <button
                            onClick={() => { setMostrarImport(false); resetear(); }}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="p-6 space-y-5">

                        {/* ── Instrucciones + descarga plantilla ── */}
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700">
                            <div className="flex-1 text-sm text-blue-800 dark:text-blue-200 space-y-1">
                                <p className="font-semibold">¿Cómo preparar el archivo?</p>
                                <ul className="list-disc list-inside text-blue-700 dark:text-blue-300 space-y-0.5">
                                    <li>Descarga la plantilla CSV y rellénala (respeta las cabeceras)</li>
                                    <li><strong>nombre</strong> obligatorio + al menos <em>teléfono, email o whatsapp</em></li>
                                    <li>Suscripciones: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded text-xs">si</code> o <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded text-xs">no</code></li>
                                    <li>El archivo se procesa en Google Cloud — hasta <strong>50 MB</strong></li>
                                </ul>
                            </div>
                            <button
                                id="btn-descargar-plantilla"
                                onClick={descargarPlantilla}
                                className="flex items-center gap-2 shrink-0 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition text-sm"
                            >
                                <Download size={15} />
                                Descargar plantilla
                            </button>
                        </div>

                        {/* ── Panel de error inline ── */}
                        {errorPanel && (
                            <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-300">
                                <XCircle size={18} className="flex-shrink-0 mt-0.5" />
                                <span className="flex-1">{errorPanel}</span>
                                <button onClick={() => setErrorPanel(null)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
                            </div>
                        )}

                        {/* ── PASO 1: Nombre de la lista + selección de archivo ── */}
                        {!jobId && (
                            <>
                                {/* Campo nombre de lista */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Nombre de la lista <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={nombreLista}
                                        onChange={e => setNombreLista(e.target.value)}
                                        placeholder="Ej: Residentes Madrid Zona 1"
                                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-gray-100 text-sm"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Este nombre identificará la lista en el sistema y en el selector de alertas.</p>
                                </div>
                                {/* Dropzone */}
                                <div
                                    id="dropzone-csv"
                                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                                    onDragLeave={() => setDragging(false)}
                                    onDrop={onDrop}
                                    onClick={() => !subiendo && inputRef.current?.click()}
                                    className={`cursor-pointer border-2 border-dashed rounded-xl p-8 text-center transition select-none
                                        ${dragging
                                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                                            : 'border-gray-300 dark:border-gray-600 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/10'
                                        }`}
                                >
                                    <input
                                        ref={inputRef}
                                        type="file"
                                        accept=".csv,text/csv"
                                        className="hidden"
                                        onChange={onFileChange}
                                    />
                                    <FileText className="mx-auto mb-3 text-gray-400 dark:text-gray-500" size={36} />
                                    {archivoNombre ? (
                                        <p className="text-emerald-700 dark:text-emerald-300 font-semibold">{archivoNombre}</p>
                                    ) : (
                                        <>
                                            <p className="font-semibold text-gray-700 dark:text-gray-200">Arrastra tu archivo CSV aquí</p>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                                                className="mt-3 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition shadow-sm"
                                            >
                                                Seleccionar Archivo
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Preview de filas */}
                                {preview.length > 0 && (
                                    <div className="space-y-3">

                                        {/* Badges resumen */}
                                        <div className="flex flex-wrap gap-2 items-center">
                                            <span className="flex items-center gap-1.5 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-medium">
                                                <CheckCircle size={14} /> {filasBuenas} válidas
                                            </span>
                                            {erroresLocal.length > 0 && (
                                                <span className="flex items-center gap-1.5 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-sm font-medium">
                                                    <XCircle size={14} /> {erroresLocal.length} con error
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-sm font-medium">
                                                <FileText size={14} /> {preview.length} filas totales
                                            </span>
                                        </div>

                                        {/* Tabla preview */}
                                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 text-sm">
                                            <table className="w-full">
                                                <thead className="bg-gray-50 dark:bg-gray-700">
                                                    <tr>
                                                        {['Fila', 'Nombre', 'Email', 'Teléfono', 'Tipo', 'Estado'].map(h => (
                                                            <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                                    {preview.slice(0, 10).map((row, i) => {
                                                        const tieneError = erroresLocal.some(e => e.fila === i + 2);
                                                        return (
                                                            <tr key={i} className={tieneError ? 'bg-red-50 dark:bg-red-900/10' : 'bg-white dark:bg-gray-800'}>
                                                                <td className="px-3 py-2 text-gray-400 dark:text-gray-500">{i + 2}</td>
                                                                <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-100">
                                                                    {row.nombre || <em className="text-red-500 text-xs">vacío</em>}
                                                                </td>
                                                                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{row.email || '-'}</td>
                                                                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{row.telefono || '-'}</td>
                                                                <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{row.tipo_usuario || 'Residente'}</td>
                                                                <td className="px-3 py-2">
                                                                    {tieneError
                                                                        ? <span className="flex items-center gap-1 text-red-500 text-xs font-medium"><XCircle size={13} /> Error</span>
                                                                        : <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium"><CheckCircle size={13} /> OK</span>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                            {preview.length > 10 && (
                                                <p className="text-center text-xs text-gray-400 py-2">
                                                    Mostrando 10 de {preview.length} filas
                                                </p>
                                            )}
                                        </div>

                                        {/* Errores locales */}
                                        {erroresLocal.length > 0 && (
                                            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3">
                                                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2 mb-1">
                                                    <AlertTriangle size={14} /> Estas filas se omitirán:
                                                </p>
                                                <ul className="text-xs text-amber-700 dark:text-amber-400 list-disc list-inside space-y-0.5">
                                                    {erroresLocal.slice(0, 8).map((e, i) => (
                                                        <li key={i}>Fila {e.fila}: {e.motivo}</li>
                                                    ))}
                                                    {erroresLocal.length > 8 && <li>…y {erroresLocal.length - 8} más</li>}
                                                </ul>
                                            </div>
                                        )}

                                        {/* Botones */}
                                        <div className="flex gap-3 pt-1 flex-wrap">
                                            <button
                                                id="btn-confirmar-importacion"
                                                onClick={handleImportar}
                                                disabled={subiendo || filasBuenas === 0}
                                                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-semibold transition"
                                            >
                                                {subiendo ? (
                                                    <><Loader2 size={18} className="animate-spin" /> Subiendo a Google Cloud…</>
                                                ) : (
                                                    <><Upload size={18} /> Importar {filasBuenas} contacto{filasBuenas !== 1 ? 's' : ''} <ChevronRight size={16} /></>
                                                )}
                                            </button>
                                            <button
                                                onClick={resetear}
                                                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-sm"
                                            >
                                                <RotateCcw size={15} /> Cambiar archivo
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* ── PASO 2: Progreso del job en Google Cloud ── */}
                        {jobId && jobEstado && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-3">
                                        {estaEnProgreso && <Loader2 size={18} className="animate-spin text-blue-500" />}
                                        <span className="font-semibold text-gray-800 dark:text-gray-100">
                                            Job #{jobId} — {archivoNombre}
                                        </span>
                                    </div>
                                    <BadgeEstado estado={jobEstado.status} />
                                </div>

                                {/* Barra de progreso */}
                                <BarraProgreso
                                    pct={jobEstado.progreso_pct ?? 0}
                                    estado={jobEstado.status}
                                />

                                {/* Contadores */}
                                <div className="grid grid-cols-3 gap-3 text-center">
                                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                        <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                                            {jobEstado.processed_records}
                                        </p>
                                        <p className="text-xs text-green-600 dark:text-green-400 font-medium mt-0.5">Insertados</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                        <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                                            {jobEstado.failed_records}
                                        </p>
                                        <p className="text-xs text-red-500 dark:text-red-400 font-medium mt-0.5">Con error</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                        <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                                            {jobEstado.total_records}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">Total</p>
                                    </div>
                                </div>

                                {/* Mensaje de estado */}
                                {estaEnProgreso && (
                                    <p className="text-sm text-blue-600 dark:text-blue-400 flex items-center gap-2">
                                        <Loader2 size={14} className="animate-spin" />
                                        Google Cloud está procesando el archivo. Actualizando cada 3 segundos…
                                    </p>
                                )}

                                {/* Resultado final */}
                                {esFinalizado && (
                                    <div className={`rounded-lg p-4 border ${
                                        jobEstado.status === 'COMPLETED'
                                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                            : jobEstado.status === 'PARTIAL_SUCCESS'
                                                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                                                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                    }`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            {jobEstado.status === 'COMPLETED' && <CheckCircle className="text-green-600 dark:text-green-400" size={20} />}
                                            {jobEstado.status === 'PARTIAL_SUCCESS' && <AlertTriangle className="text-amber-600 dark:text-amber-400" size={20} />}
                                            {jobEstado.status === 'FAILED' && <XCircle className="text-red-600 dark:text-red-400" size={20} />}
                                            <span className={`font-semibold ${
                                                jobEstado.status === 'COMPLETED'       ? 'text-green-700 dark:text-green-300' :
                                                jobEstado.status === 'PARTIAL_SUCCESS' ? 'text-amber-700 dark:text-amber-300' :
                                                                                         'text-red-700 dark:text-red-300'
                                            }`}>
                                                {jobEstado.status === 'COMPLETED'       ? 'Importación completada con éxito' :
                                                 jobEstado.status === 'PARTIAL_SUCCESS' ? 'Importación parcial — algunos registros fallaron' :
                                                                                          'La importación falló'}
                                            </span>
                                        </div>
                                        {jobEstado.error_file_url && (
                                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                Archivo de errores guardado en Google Cloud Storage:<br />
                                                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">{jobEstado.error_file_url}</code>
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Botón nueva importación */}
                                {esFinalizado && (
                                    <button
                                        onClick={resetear}
                                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg font-semibold transition text-sm"
                                    >
                                        <RotateCcw size={15} /> Nueva importación
                                    </button>
                                )}
                            </div>
                        )}

                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                FORMULARIO DE CONTACTO INDIVIDUAL
            ══════════════════════════════════════════════════════════════ */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                    {contactoEditando ? 'Editar Contacto' : 'Nuevo Contacto'}
                </h3>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nombre *</label>
                            <input type="text" value={nuevoContacto.nombre}
                                onChange={e => setNuevoContacto({ ...nuevoContacto, nombre: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="Nombre completo" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                            <input type="email" value={nuevoContacto.email}
                                onChange={e => setNuevoContacto({ ...nuevoContacto, email: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="email@ejemplo.com" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teléfono *</label>
                            <input type="tel" value={nuevoContacto.telefono}
                                onChange={e => setNuevoContacto({ ...nuevoContacto, telefono: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="+34666777888" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">WhatsApp</label>
                            <input type="tel" value={nuevoContacto.whatsapp}
                                onChange={e => setNuevoContacto({ ...nuevoContacto, whatsapp: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="+34666777888" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Usuario *</label>
                            <CustomSelect
                                value={nuevoContacto.tipo_usuario}
                                onChange={v => setNuevoContacto({ ...nuevoContacto, tipo_usuario: v })}
                                options={[
                                    { value: 'Residente',   label: 'Residente' },
                                    { value: 'Empresa',     label: 'Empresa' },
                                    { value: 'Institución', label: 'Institución' },
                                    { value: 'Colaborador', label: 'Colaborador' },
                                ]}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Punto Crítico</label>
                            <CustomSelect
                                value={nuevoContacto.tipo_punto_critico}
                                onChange={v => setNuevoContacto({ ...nuevoContacto, tipo_punto_critico: v })}
                                options={[
                                    { value: 'Hospital',        label: 'Hospital' },
                                    { value: 'Colegio',         label: 'Colegio' },
                                    { value: 'Residencia',      label: 'Residencia' },
                                    { value: 'Centro Comercial',label: 'Centro Comercial' },
                                    { value: 'Camping',         label: 'Camping' },
                                ]}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ciudad</label>
                            <input type="text" value={nuevoContacto.ciudad}
                                onChange={e => setNuevoContacto({ ...nuevoContacto, ciudad: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="Málaga" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">País</label>
                            <input type="text" value={nuevoContacto.pais}
                                onChange={e => setNuevoContacto({ ...nuevoContacto, pais: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 dark:text-gray-100"
                                placeholder="España" />
                        </div>

                        {/* --- Campos Málaga Responde --- */}
                        <div className="md:col-span-2 border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
                            <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Datos Málaga Responde</h4>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Zona (Distrito / Barriada de Málaga) *
                            </label>
                            <CustomSelect
                                value={normalizarZonaMalaga(nuevoContacto.zona || nuevoContacto.distrito_municipal || '')}
                                onChange={v => setNuevoContacto({ ...nuevoContacto, zona: v, distrito_municipal: v })}
                                options={ZONAS_MALAGA_OPTIONS}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rol</label>
                            <CustomSelect
                                value={nuevoContacto.rol || 'poblacion'}
                                onChange={v => setNuevoContacto({ ...nuevoContacto, rol: v })}
                                options={[
                                    { value: 'poblacion', label: 'Población' },
                                    { value: 'gestor', label: 'Gestor de Emergencia' },
                                    { value: 'direccion', label: 'Dirección de Emergencia' }
                                ]}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Vulnerabilidad</label>
                            <CustomSelect
                                value={nuevoContacto.tipo_vulnerabilidad || 'ninguna'}
                                onChange={v => setNuevoContacto({ ...nuevoContacto, tipo_vulnerabilidad: v })}
                                options={[
                                    { value: 'ninguna', label: 'Ninguna' },
                                    { value: 'movilidad_reducida', label: 'Movilidad Reducida' },
                                    { value: 'dependiente', label: 'Persona Dependiente' },
                                    { value: 'menor', label: 'Menor de Edad' },
                                    { value: 'mayor_65', label: 'Mayor de 65 años' },
                                    { value: 'discapacidad_sensorial', label: 'Discapacidad Sensorial' },
                                    { value: 'otra', label: 'Otra' }
                                ]}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Medio de Comunicación Preferido</label>
                            <CustomSelect
                                value={nuevoContacto.medio_comunicacion_preferido || 'whatsapp'}
                                onChange={v => setNuevoContacto({ ...nuevoContacto, medio_comunicacion_preferido: v })}
                                options={[
                                    { value: 'whatsapp', label: 'WhatsApp' },
                                    { value: 'sms', label: 'SMS' },
                                    { value: 'email', label: 'Email' }
                                ]}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Suscripciones para envíos por:</label>
                        <div className="flex gap-4 flex-wrap">
                            {[
                                { key: 'suscrito_email', label: 'Email' },
                                { key: 'suscrito_sms', label: 'SMS' },
                                { key: 'suscrito_whatsapp', label: 'WhatsApp' },
                            ].map(({ key, label }) => (
                                <label key={key} className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox"
                                        checked={nuevoContacto[key]}
                                        onChange={e => setNuevoContacto({ ...nuevoContacto, [key]: e.target.checked })}
                                        className="w-4 h-4 text-primary-600 rounded" />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-3 flex-wrap">
                        <button onClick={guardarContacto}
                            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition flex items-center gap-2">
                            {contactoEditando
                                ? <><Edit2 size={18} />Guardar Cambios</>
                                : <><Plus size={18} />Guardar Contacto</>}
                        </button>
                        {contactoEditando && (
                            <button onClick={cancelarEdicionContacto}
                                className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-6 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition">
                                Cancelar
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                PESTAÑAS: CONTACTOS INDIVIDUALES | LISTAS
            ══════════════════════════════════════════════════════════════ */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">

                {/* Pestañas */}
                <div className="flex border-b dark:border-gray-700">
                    <button
                        onClick={() => { setTabActiva('contactos'); setListaSeleccionada(null); }}
                        className={`flex-1 py-3 px-2.5 sm:px-5 text-xs sm:text-sm font-semibold transition ${
                            tabActiva === 'contactos'
                                ? 'border-b-2 border-primary-600 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                    >
                        Contactos individuales
                        <span className="ml-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">{contactos.length}</span>
                    </button>
                    <button
                        onClick={() => setTabActiva('listas')}
                        className={`flex-1 py-3 px-2.5 sm:px-5 text-xs sm:text-sm font-semibold transition ${
                            tabActiva === 'listas'
                                ? 'border-b-2 border-emerald-600 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                    >
                        Listas / Archivos
                        <span className="ml-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">{listas.length}</span>
                    </button>
                </div>

                <div className="p-4 sm:p-6">

                {/* ── TAB: Contactos individuales ── */}
                {tabActiva === 'contactos' && (
                    <>
                        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                            <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300">
                                Todos los contactos
                                <span className="ml-2 text-sm font-normal text-gray-400">({contactos.length} registros)</span>
                            </h3>
                            {/* Filtro por Zona + Buscador Contactos */}
                            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                <select
                                    value={filtroZona}
                                    onChange={e => setFiltroZona(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary-400 focus:outline-none w-full sm:w-60"
                                >
                                    <option value="">Todas las zonas (14 zonas)</option>
                                    {ZONAS_MALAGA.map(z => (
                                        <option key={z} value={z}>{z}</option>
                                    ))}
                                </select>
                                <div className="relative w-full sm:w-auto">
                                    <input
                                        type="text"
                                        value={busquedaContactos}
                                        onChange={e => setBusquedaContactos(e.target.value)}
                                        placeholder="Buscar contacto..."
                                        className="pl-4 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-primary-400 focus:outline-none w-full sm:w-56"
                                    />
                                </div>
                            </div>
                        </div>

                        {contactos.length === 0 ? (
                            <p className="text-gray-500 dark:text-gray-400 text-center py-10">No hay contactos registrados</p>
                        ) : (
                            <div className="overflow-x-auto -mx-4 sm:mx-0">
                                <table className="w-full min-w-[720px] text-sm">
                                    <thead>
                                        <tr className="border-b dark:border-gray-700">
                                            {['Nombre', 'Email / Teléfono', 'Zona (Málaga Responde)', 'Tipo Usuario', 'Punto Crítico', 'Estado', 'Acciones'].map(h => (
                                                <th key={h} className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contactos
                                            .filter(c => {
                                                const zonaContacto = normalizarZonaMalaga(c.zona || c.distrito_municipal || '');
                                                if (filtroZona && zonaContacto !== filtroZona) return false;
                                                if (!busquedaContactos) return true;
                                                const q = busquedaContactos.toLowerCase();
                                                return (
                                                    c.nombre?.toLowerCase().includes(q) ||
                                                    c.email?.toLowerCase().includes(q) ||
                                                    c.telefono?.toLowerCase().includes(q) ||
                                                    zonaContacto.toLowerCase().includes(q)
                                                );
                                            })
                                            .map(c => {
                                                const zonaMostrar = normalizarZonaMalaga(c.zona || c.distrito_municipal || '');
                                                return (
                                            <tr key={c.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition">
                                                <td className="py-3 px-4 font-medium text-gray-800 dark:text-gray-100">{c.nombre}</td>
                                                <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                                                    <div>{c.email || '-'}</div>
                                                    {c.telefono && <div className="text-xs text-gray-400">{c.telefono}</div>}
                                                </td>
                                                <td className="py-3 px-4">
                                                    {zonaMostrar ? (
                                                        <span className="px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs rounded-full font-medium">
                                                            {zonaMostrar}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">Sin asignar</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{c.tipo_usuario}</td>
                                                <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{c.tipo_punto_critico || '-'}</td>
                                                <td className="py-3 px-4">
                                                    {c.activo
                                                        ? <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full font-medium">Activo</span>
                                                        : <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs rounded-full font-medium">Inactivo</span>
                                                    }
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex gap-3 flex-wrap">
                                                        <button onClick={() => iniciarEdicionContacto(c)}
                                                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 flex items-center gap-1">
                                                            <Edit2 size={15} /> Editar
                                                        </button>
                                                        {c.activo
                                                            ? <button onClick={() => darDeBajaContacto(c.id)}
                                                                className="text-orange-500 dark:text-orange-400 hover:text-orange-700 flex items-center gap-1">
                                                                <UserX size={15} /> Dar de baja
                                                              </button>
                                                            : <button onClick={() => reactivarContacto(c.id)}
                                                                className="text-green-500 dark:text-green-400 hover:text-green-700 flex items-center gap-1">
                                                                <UserCheck size={15} /> Reactivar
                                                              </button>
                                                        }
                                                        <button onClick={() => eliminarContacto(c.id)}
                                                            className="text-red-600 dark:text-red-400 hover:text-red-800 flex items-center gap-1">
                                                            <Trash2 size={15} /> Eliminar
                                                        </button>
                                                        <button onClick={() => setModalListaContactoId(c.id)}
                                                            className="text-purple-600 dark:text-purple-400 hover:text-purple-800 flex items-center gap-1">
                                                            <List size={15} /> A lista
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {/* ── TAB: Listas / Archivos ── */}
                {tabActiva === 'listas' && (
                    <>
                        {!listaSeleccionada ? (
                            // Vista: catálogo de listas
                            <>
                                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                                    <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300">
                                        Listas importadas
                                        <span className="ml-2 text-sm font-normal text-gray-400">({listas.length})</span>
                                    </h3>
                                    {/* Buscador */}
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={busquedaListas}
                                            onChange={e => setBusquedaListas(e.target.value)}
                                            placeholder="Buscar lista…"
                                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-emerald-400 focus:outline-none w-56"
                                        />
                                    </div>
                                </div>

                                {listas.length === 0 ? (
                                    <div className="text-center py-14 text-gray-400">
                                        <FileText size={40} className="mx-auto mb-3 opacity-40" />
                                        <p className="font-medium">Aún no hay listas importadas</p>
                                        <p className="text-sm mt-1">Usa el botón "Importar CSV" para crear tu primera lista.</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {listas
                                            .filter(l => l.nombre?.toLowerCase().includes(busquedaListas.toLowerCase()))
                                            .map(lista => (
                                                <div
                                                    key={lista.grupo_id || lista.job_id}
                                                    className="p-4 border dark:border-gray-700 rounded-xl hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-600 transition cursor-pointer group"
                                                    onClick={() => verContactosDeLista(lista)}
                                                >
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: lista.color || '#10b981' }} />
                                                                                        <BadgeEstado estado={lista.status} />
                                                    </div>
                                                    <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mt-1 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition">{lista.nombre}</h4>
                                                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                        <span>{lista.total_contactos || lista.processed_records || 0} contactos</span>
                                                        <span>·</span>
                                                        <span>{lista.fecha_importacion ? new Date(lista.fecha_importacion).toLocaleDateString('es-ES') : '—'}</span>
                                                    </div>
                                                    <div className="mt-3 text-xs text-emerald-600 dark:text-emerald-400 font-medium opacity-0 group-hover:opacity-100 transition">
                                                        Ver contactos
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                )}
                            </>
                        ) : (
                            // Vista: contactos de una lista
                            <>
                                <div className="flex items-center gap-3 mb-4 flex-wrap">
                                    <button
                                        onClick={() => { setListaSeleccionada(null); setContactosDeLista([]); }}
                                        className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 transition"
                                    >
                                        Volver a listas
                                    </button>
                                    <span className="text-gray-300 dark:text-gray-600">|</span>
                                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                                        {listaSeleccionada.nombre}
                                    </h3>
                                    <BadgeEstado estado={listaSeleccionada.status} />
                                </div>

                                {cargandoContactosLista ? (
                                    <div className="flex items-center justify-center py-12 gap-3 text-gray-400">
                                        <Loader2 size={22} className="animate-spin" />
                                        <span>Cargando contactos…</span>
                                    </div>
                                ) : contactosDeLista.length === 0 ? (
                                    <p className="text-center text-gray-400 py-10">Esta lista aún no tiene contactos procesados.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <p className="text-xs text-gray-400 mb-3">{contactosDeLista.length} contactos en esta lista</p>
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b dark:border-gray-700">
                                                    {['Nombre', 'Email', 'Teléfono', 'Tipo', 'Ciudad', 'Estado'].map(h => (
                                                        <th key={h} className="text-left py-2 px-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wide">{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {contactosDeLista.map(c => (
                                                    <tr key={c.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition">
                                                        <td className="py-2 px-3 font-medium text-gray-800 dark:text-gray-100">{c.nombre}</td>
                                                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs">{c.email || '-'}</td>
                                                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs">{c.telefono || '-'}</td>
                                                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs">{c.tipo_usuario}</td>
                                                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs">{c.ciudad || '-'}</td>
                                                        <td className="py-2 px-3">
                                                            {c.activo
                                                                ? <span className="text-green-600 dark:text-green-400 text-xs font-medium">Activo</span>
                                                                : <span className="text-red-500 text-xs font-medium">Inactivo</span>
                                                            }
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    
            {/* Modal: Añadir contacto a lista */}
            {modalListaContactoId && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setModalListaContactoId(null)}>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">Añadir contacto a una lista</h3>
                        <select
                            value={listaSeleccionadaParaAnadir}
                            onChange={e => setListaSeleccionadaParaAnadir(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-gray-100 mb-4"
                        >
                            <option value="">-- Selecciona una lista --</option>
                            {listas.map(l => (
                                <option key={l.id} value={l.id}>{l.nombre} ({l.total_contactos || 0} contactos)</option>
                            ))}
                        </select>
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setModalListaContactoId(null)}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition">
                                Cancelar
                            </button>
                            <button onClick={handleAnadirALista} disabled={!listaSeleccionadaParaAnadir || anadiendo}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50">
                                {anadiendo ? 'Añadiendo...' : 'Añadir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
                )}

                </div>
            </div>
        </div>
    );
};

export default ContactsManager;
